import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Trophy,
  AlertTriangle,
  Layers,
  LineChart,
  BarChart2,
} from 'lucide-react';
import type {
  BrakingCornerConsistency,
  BrakingOverallSummary,
  BrakingZoneData,
  SessionSummary,
} from '@/types/telemetry';
import { useI18n } from '@/context/I18nContext';
import { createMetricTimelineChart, createSessionMedianChart } from '@/lib/charts';
import { formatDynamicCornerLabel } from '@/lib/spline';

interface BrakingOverallHeaderProps {
  zones: BrakingZoneData[];
  overallSummary?: BrakingOverallSummary;
  sessions?: SessionSummary[];
  selectedTrack?: string;
  onSelectZone?: (index: number) => void;
  activeZone?: number;
}

export function BrakingOverallHeader({
  zones,
  overallSummary,
  sessions = [],
  selectedTrack,
  onSelectZone,
  activeZone,
}: BrakingOverallHeaderProps) {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<'timeline' | 'sessions'>('timeline');
  const timelineChartRef = useRef<HTMLDivElement | null>(null);
  const sessionChartRef = useRef<HTMLDivElement | null>(null);

  const uniqueLaps = overallSummary?.lapSummaries || [];
  const sessionBoundaries = overallSummary?.sessionBoundaries || [];
  const overallTrackScore = overallSummary?.overallTrackScore ?? 70;
  const overallTier = overallSummary?.overallTier || 'good';
  const sessionMedians = overallSummary?.sessionSummaries || [];

  const formatCornerLabel = (c?: BrakingCornerConsistency | null) => {
    if (!c) return null;
    const turnNum = (c.zoneIndex ?? 0) + 1;
    const cornerName = formatDynamicCornerLabel(
      c.name,
      turnNum,
      t('corner.turn') || 'Turn'
    );
    return {
      ...c,
      zoneIndex: c.zoneIndex ?? 0,
      orderIndex: turnNum,
      name: cornerName,
      medianScore: c.medianScore ?? 0,
      stdDev: c.stdDev ?? 0,
      consistencyPct: c.consistencyPct ?? 85,
    };
  };

  const mostConsistent = useMemo(
    () => formatCornerLabel(overallSummary?.mostConsistentCorner),
    [overallSummary?.mostConsistentCorner, t]
  );

  const leastConsistent = useMemo(
    () => formatCornerLabel(overallSummary?.leastConsistentCorner),
    [overallSummary?.leastConsistentCorner, t]
  );

  // 4. Render Lap-by-Lap Timeline Chart
  useEffect(() => {
    if (activeTab !== 'timeline' || !timelineChartRef.current || uniqueLaps.length === 0) return;
    timelineChartRef.current.innerHTML = '';

    const yData = uniqueLaps.map(l => l.overallScore);
    const timer = requestAnimationFrame(() => {
      if (!timelineChartRef.current) return;
      createMetricTimelineChart(timelineChartRef.current, yData, sessionBoundaries, {
        label: t('braking.overallScore'),
        color: '#7c5cfc',
        yLabel: '%',
        yFormat: v => v.toFixed(0) + '%',
        reverseGoodness: true,
      });
    });

    return () => cancelAnimationFrame(timer);
  }, [activeTab, uniqueLaps, sessionBoundaries, t]);

  // 5. Render Session Median Bar Chart
  useEffect(() => {
    if (activeTab !== 'sessions' || !sessionChartRef.current || sessionMedians.length === 0) return;
    sessionChartRef.current.innerHTML = '';

    const timer = requestAnimationFrame(() => {
      if (!sessionChartRef.current) return;

      const mappedForChart = sessionMedians.map(s => ({
        sessionId: s.sessionId,
        sessionDate: s.sessionDate,
        sessionTime: s.sessionTime,
        medianDurationMs: s.medianScore,
      }));

      const sessionMeta = sessions.map(s => ({ id: s.id, date: s.date, time: s.time }));

      createSessionMedianChart(
        sessionChartRef.current,
        mappedForChart as any,
        (m: any) => m.medianDurationMs,
        sessionMeta,
        {
          label: t('braking.sessionComparison'),
          color: '#38bdf8',
          yLabel: '%',
          yFormat: (v: number) => v.toFixed(0) + '%',
          reverseGoodness: true,
        }
      );
    });

    return () => cancelAnimationFrame(timer);
  }, [activeTab, sessionMedians, sessions, t]);

  const tierBadgeClass =
    overallTier === 'optimal'
      ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
      : overallTier === 'good'
      ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
      : 'bg-red-500/15 text-red-400 border-red-500/30';

  const tierText =
    overallTier === 'optimal'
      ? t('braking.scoreOptimal')
      : overallTier === 'good'
      ? t('braking.scoreGood')
      : t('braking.scoreNeedsWork');

  return (
    <div className="flex flex-col gap-5">
      {/* 4-Card Aggregate KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Overall Trail-Braking Score */}
        <div className="bg-bg-surface border border-border-subtle rounded-xl p-4 flex flex-col justify-between shadow-xs">
          <div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                {t('braking.overallScore')}
              </span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${tierBadgeClass}`}>
                {tierText}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-extrabold font-mono text-text-primary tracking-tight">
                {overallTrackScore.toFixed(0)}%
              </span>
              <span className="text-xs text-text-muted">
                {uniqueLaps.length} {t('braking.allLaps')}
              </span>
            </div>
          </div>
          <div className="mt-3 pt-2.5 border-t border-border-subtle/80 flex items-center justify-between text-[11px] text-text-muted">
            <span>{sessionMedians.length} {sessionMedians.length === 1 ? 'Session' : 'Sessions'}</span>
            <span className="text-accent font-medium">{zones.length} {t('braking.zones')}</span>
          </div>
        </div>

        {/* Card 2: Most Consistent Corner */}
        {mostConsistent && (
          <div
            onClick={() => onSelectZone?.(mostConsistent.zoneIndex)}
            className={`
              bg-bg-surface border rounded-xl p-4 flex flex-col justify-between shadow-xs transition-all cursor-pointer group
              ${
                activeZone === mostConsistent.zoneIndex
                  ? 'border-emerald-500/50 bg-emerald-500/5 shadow-sm'
                  : 'border-border-subtle hover:border-emerald-500/30'
              }
            `}
          >
            <div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Trophy size={13} />
                  {t('braking.mostConsistentCorner')}
                </span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 font-bold border border-emerald-500/30">
                  {mostConsistent.consistencyPct}%
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-xl font-bold font-mono text-text-primary tracking-tight group-hover:text-emerald-400 transition-colors">
                  {mostConsistent.name}
                </span>
                <span className="text-xs font-mono text-text-secondary font-medium">
                  {mostConsistent.medianScore.toFixed(0)}% {t('braking.scoreUnit')}
                </span>
              </div>
            </div>
            <div className="mt-3 pt-2.5 border-t border-border-subtle/80 flex items-center justify-between text-[11px] text-text-muted">
              <span>{t('braking.stdDevLabel')}: ±{mostConsistent.stdDev.toFixed(1)}%</span>
              <span className="text-emerald-400 text-[10px] group-hover:underline flex items-center gap-0.5 font-medium">
                {t('braking.selectCornerHint')} →
              </span>
            </div>
          </div>
        )}

        {/* Card 3: Least Consistent Corner */}
        {leastConsistent && (
          <div
            onClick={() => onSelectZone?.(leastConsistent.zoneIndex)}
            className={`
              bg-bg-surface border rounded-xl p-4 flex flex-col justify-between shadow-xs transition-all cursor-pointer group
              ${
                activeZone === leastConsistent.zoneIndex
                  ? 'border-amber-500/50 bg-amber-500/5 shadow-sm'
                  : 'border-border-subtle hover:border-amber-500/30'
              }
            `}
          >
            <div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                  <AlertTriangle size={13} />
                  {t('braking.leastConsistentCorner')}
                </span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-400 font-bold border border-amber-500/30">
                  {leastConsistent.consistencyPct}%
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-xl font-bold font-mono text-text-primary tracking-tight group-hover:text-amber-400 transition-colors">
                  {leastConsistent.name}
                </span>
                <span className="text-xs font-mono text-text-secondary font-medium">
                  {leastConsistent.medianScore.toFixed(0)}% {t('braking.scoreUnit')}
                </span>
              </div>
            </div>
            <div className="mt-3 pt-2.5 border-t border-border-subtle/80 flex items-center justify-between text-[11px] text-text-muted">
              <span>{t('braking.stdDevLabel')}: ±{leastConsistent.stdDev.toFixed(1)}%</span>
              <span className="text-amber-400 text-[10px] group-hover:underline flex items-center gap-0.5 font-medium">
                {t('braking.selectCornerHint')} →
              </span>
            </div>
          </div>
        )}

        {/* Card 4: Track Overview Context */}
        <div className="bg-bg-surface border border-border-subtle rounded-xl p-4 flex flex-col justify-between shadow-xs">
          <div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider flex items-center gap-1.5">
                <Layers size={13} className="text-accent" />
                {selectedTrack || t('braking.trackOverview')}
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent/10 border border-accent/20 text-accent font-medium">
                {t('braking.cornersCount', { count: zones.length })}
              </span>
            </div>
            <div className="text-sm font-semibold text-text-primary truncate" title={selectedTrack}>
              {selectedTrack || t('braking.trackAnalysis')}
            </div>
            <p className="text-[11px] text-text-muted mt-0.5">
              {t('braking.overallScoreDesc')}
            </p>
          </div>
          <div className="mt-3 pt-2.5 border-t border-border-subtle/80 flex items-center justify-between text-[11px] text-text-muted">
            <span>{t('braking.lapsTotal', { count: uniqueLaps.length })}</span>
            <span className="text-text-secondary font-mono font-medium">
              {t('sectorPanel.median')} {overallTrackScore.toFixed(0)}%
            </span>
          </div>
        </div>
      </div>

      {/* Tabbed Chart Card */}
      <div className="card p-5">
        {/* Chart Card Header with Tab Switcher */}
        <div className="flex items-center justify-between flex-wrap gap-3 pb-3 mb-4 border-b border-border-subtle">
          <div className="flex items-center gap-2">
            <LineChart size={17} className="text-accent" />
            <span className="text-sm font-bold text-text-primary">
              {activeTab === 'timeline' ? t('braking.lapTimeline') : t('braking.sessionComparison')}
            </span>
          </div>

          {/* Tabs */}
          <div className="p-1 rounded-xl bg-bg-surface border border-border-subtle flex gap-1">
            <button
              onClick={() => setActiveTab('timeline')}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer
                ${
                  activeTab === 'timeline'
                    ? 'bg-accent/20 text-accent border border-accent/30 shadow-xs'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover border border-transparent'
                }
              `}
            >
              <LineChart size={13} />
              <span>{t('braking.lapTimeline')}</span>
            </button>

            <button
              onClick={() => setActiveTab('sessions')}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer
                ${
                  activeTab === 'sessions'
                    ? 'bg-accent/20 text-accent border border-accent/30 shadow-xs'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover border border-transparent'
                }
              `}
            >
              <BarChart2 size={13} />
              <span>{t('braking.sessionComparison')}</span>
            </button>
          </div>
        </div>

        {/* Chart Viewport */}
        {activeTab === 'timeline' && (
          <div className="w-full">
            <div ref={timelineChartRef} className="chart-container w-full min-h-[260px]" />
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="w-full">
            <div ref={sessionChartRef} className="chart-container w-full min-h-[220px]" />
          </div>
        )}
      </div>
    </div>
  );
}
